import {
  PolicyState,
  Program,
  RetrieveByIdRequest,
  TransactionType,
  QuoteDecision,
  GenericStatus,
  GenericEventType,
  User as CoreUser,
  Request,
  Kind,
  Policy,
  ApplicationFormData,
} from '@policyfly/protobuf'
import {
  DiffPolicyStatesRequest,
  DiffPolicyStatesResponse,
  PatrickServiceClient,
  ReadPolicyStateRequest,
  SaveApplicationRequest,
  SaveApplicationResponse,
  SpawnApplicationRequest,
  SpawnApplicationResponse,
  SubmitApplicationRequest,
  SubmitApplicationResponse,
  WorkflowRequest,
  WorkflowResponse,
} from '@policyfly/protobuf/patrick'
import { InputData_Protobuf_Bucket } from '@policyfly/protobuf/pfschema'
import { FORM_INITIAL_FLAG_PATH } from '@policyfly/utils/constants'
import { toCamelCase } from '@policyfly/utils/string'
import * as Sentry from '@sentry/browser'
import { AxiosError } from 'axios'

import { useApiStore } from '@/stores/api'
import { useAuthenticationStore } from '@/stores/authentication'
import { useFormApplicationStore } from '@/stores/form/application'
import { useProtobufStore } from '@/stores/protobuf'
import { useSettingsStore } from '@/stores/settings'

import { api } from '@/api'
import { devtools } from '@/plugins/devtools/api'
import { objectToFormData } from '@/utils/api'
import { hasMatchingId } from '@/utils/compare'
import {
  applicationStatusToGenericStatus,
  extractAnyWrapper,
  patrickUserToCoreUser,
  policyStateToApplication,
} from '@/utils/protobuf'

import type { UseRaterReturnValue } from '@/composables/rater'
import type Payload from '@/lib/Payload'
import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'
import type { DiffData, FormDataDiffData } from '@/stores/diff'
import type {
  DiffResult,
  RunWorkflowRequest,
} from '@policyfly/protobuf'
import type { User, UserProgram } from '@policyfly/protobuf/patrick'
import type { ApplicationFormType } from '@policyfly/schema/types/schemas/application'
import type { ApplicationStatus } from '@policyfly/types/application'
import type { PrimitiveValue } from '@policyfly/types/common'
import type { APIPayloadResponse } from '@policyfly/utils/types'
import type { AxiosResponseHeaders } from 'axios'
import type {
  APIApplication,
  ApplicationKind,
  APIApplicationRaw,
} from 'types/application'
import type { APIPolicy } from 'types/policy'
import type { paths } from 'types/swagger'

/**
 * A callback function that handles the setting of state and legacy rating operation after data is saved.
 * This is only called during the submission of a django application.
 */
export type RateOnSubmit = (json: APIApplication | undefined, responses: APIPayloadResponse[]) => ReturnType<UseRaterReturnValue['rate']>
export interface SubmitResponse {
  policyState?: PolicyState
  policy?: APIPolicy
  applicationFormData?: ApplicationFormData
  json: APIApplication | APIApplicationRaw
  status: GenericStatus
}
export interface SubmitResponseProtobuf {
  policyState: PolicyState
  applicationFormData: ApplicationFormData
}
export interface ApplicationApiEndpoints {
  /**
   * Approves an Endorsement Application
   */
  approve: (id: number) => Promise<SubmitResponse>
  /**
   * Archives an Application
   */
  archive: (id: number) => Promise<void>
  /**
   * Set which user an Application is assigned to
   */
  assign: (id: number, assignee: User | false) => Promise<SubmitResponse>
  /**
   * Authorise an Application and transition to active Policy
   */
  authorise: (id: number) => Promise<SubmitResponse>
  /**
   * Send an Application back to Reviewing status.
   */
  backToReviewing: (id: number) => Promise<SubmitResponse>
  /**
   * Binds a quote
   */
  bindQuote: () => Promise<SubmitResponse>
  /**
   * Cancels an Application
   */
  cancel: (id: number) => Promise<SubmitResponse>
  /**
   * Does a comparison (aka "diff") of 2 Applications based on their id.
   * Returns diffs for both the PolicyState and the ApplicationFormData buckets.
   *
   * @returns A summary of changes between the 2.
   */
  compare: (baseId: number, targetId: number) => Promise<{ policyState: DiffData, formData: FormDataDiffData }>

  /**
   * Creates a new Application
   */
  create: (currentProgram: UserProgram, payload: Payload) => Promise<SubmitResponse>
  /**
   * Deletes an Application
   */
  delete: (id: number) => Promise<void>
  /**
   * Declines an Application
   */
  decline: (id: number) => Promise<SubmitResponse>
  /**
   * Transitions an Application back to draft
   */
  draft: (id: number) => Promise<SubmitResponse>
  /**
   * Transitions an Application to an active Policy
   */
  finalise: (id: number) => Promise<SubmitResponse>
  /**
   * For the application with the provided id, returns either of:
   * - Django: last modified timestamp
   * - GRPC: latest resource version
   */
  freshness: (id: number, abortController?: AbortController) => Promise<string>
  /**
   * Generates a new quote document.
   * Intended for use on the Quote Review page.
   */
  generateQuoteDoc: (id: number) => Promise<SubmitResponse>
  /**
   * Issues a Policy from an Application
   */
  issue: (id: number, body?: APIPayloadResponse[], fuzzy?: boolean) => Promise<SubmitResponse>
  /**
   * Apply issuance data to an Application without issuing the Policy
   */
  issuanceData: (id: number, body: APIPayloadResponse[], fuzzy?: boolean) => Promise<SubmitResponse>
  /**
   * Override action for an Application
   */
  lock: (id: number) => Promise<{
    policyState?: PolicyState
    json: SubmitResponse['json']
  }>
  /**
   * Mark a Quote as lost and discard the Application
   */
  lost: (id: number) => Promise<SubmitResponse>
  /**
   * Skip signing step and advance Application to pending issue
   */
  pendingIssue: (id: number, payload?: Payload, binary?: Blob) => Promise<SubmitResponse>
  /**
   * Submit the quality control response for an application
   */
  qualityControl: (id: number, action: 'PASS' | 'FAIL' | 'SKIP') => Promise<SubmitResponse>
  /**
   * Transition an Application back to Quality Control
   */
  qualityRecheck: (id: number) => Promise<SubmitResponse>
  /**
   * Submits or saves a Quote for an Application
   */
  quote: (id: number, body: FormData, kind: Kind, fuzzy?: boolean) => Promise<SubmitResponse>
  /**
   * Requests latest rating information based on the provided input.
   *
   * **NOTE: This only supports protobuf programs.**
   */
  rate: (txn: TransactionType, abortController: AbortController) => Promise<SubmitResponseProtobuf>
  /**
   * Finds & returns data about an Application with the provided id.
   */
  read: (id: number, abortController?: AbortController) => Promise<SubmitResponse>
  /**
   * Submits an Application that is no longer in DRAFT status.
   * This can be an Application in REVIEW or a later status, such as for App 2.
   */
  resubmit: (id: number, payload: Payload, type: ApplicationFormType, rate: RateOnSubmit, status: ApplicationStatus) => Promise<SubmitResponse>
  /**
   * Saves an in progress Application as draft.
   *
   * Usually called when the user manually invokes a "Save" action to differentiate from auto-saves.
   */
  save: (id: number, payload?: Payload, status?: ApplicationStatus, fuzzy?: boolean) => Promise<{
    policyState?: PolicyState
    json?: SubmitResponse['json']
    responses: APIPayloadResponse[]
    applicationFormData?: ApplicationFormData
  }>
  /**
   * Select which quote to use for an Application
   */
  selectQuote: (id: number, quoteId: string, payload?: Payload) => Promise<{
    policyState?: PolicyState
    json: SubmitResponse['json']
  }>
  /**
   * Creates a new Application from an existing one.
   * Used for starting over, endorsements, cancellations.
  */
  spawn: (id: number, type?: TransactionType, payload?: APIPayloadResponse[]) => Promise<{
    json: SubmitResponse['json']
    policy: APIPolicy
    policyState?: PolicyState
    applicationFormData?: ApplicationFormData
  }>
  /**
   * Submits an Application for the first time.
   */
  submit: (id: number, payload: Payload, kind: ApplicationKind, rate: RateOnSubmit) => Promise<SubmitResponse>
  /**
   * Saves FormData for an in progress Application
   */
  update: (id?: number, payload?: Payload, status?: ApplicationStatus, fuzzy?: boolean) => Promise<{
    policyState?: PolicyState
    json?: SubmitResponse['json']
    responses: APIPayloadResponse[]
    applicationFormData?: ApplicationFormData
  }>
  /**
   * Runs a {@link RunWorkflow workflow} on the current PolicyState.
   * Usually called when the user interacts with a button that triggers a workflow.
   *
   * Optionally accepts additional arguments to pass to the workflow.
   *
   * **NOTE: This only supports protobuf programs.**
   *
   * @example
   * // Clicking "Add Vehicle" on the Vehicles Schedule, which might trigger an NHTSA call in Lua:
   * workflow({
   *  name: 'add_vehicle'
   *  additionalArgs: {
   *    // data entered in the Add Vehicle form
   *    vehicle: { vin: '1234567890', price: '10000' },
   *    coverages: [1003, 1004],
   *  }
   * })
   */
  workflow(
    request: Pick<RunWorkflowRequest, 'name' | 'additionalArgs'>,
    options: {
      protobufOnly: false
      abortController?: AbortController
    },
  ): Promise<SubmitResponse>
  workflow(
    request: Pick<RunWorkflowRequest, 'name' | 'additionalArgs'>,
    options?: {
      protobufOnly?: boolean
      abortController?: AbortController
    },
  ): Promise<SubmitResponseProtobuf>
}

/**
 * Converts a diff result map into a list that can be used to generate the same {@link DiffData} structure
 * that the Django `compare` endpoint returns.
 *
 * - Converts the casing of the keys
 * - Extracts the `Any` grpc wrappers into their primitive value forms
 */
function processDiffResults (results: Record<string, DiffResult>): { k: string, previousValue: PrimitiveValue, currentValue: PrimitiveValue }[] {
  return Object.entries(results).map(([k, v]) => {
    const camelKey = k.split('.')
      .map((part) => toCamelCase(part))
      .join('.')
    return {
      k: camelKey,
      previousValue: extractAnyWrapper<PrimitiveValue>(v.previousValue) ?? null,
      currentValue: extractAnyWrapper<PrimitiveValue>(v.currentValue) ?? null,
    }
  })
}

/**
 * Creates default application payload for the selected program from preset values defined in config
 */
function createBaseResponses (): APIPayloadResponse[] {
  const formApplicationStore = useFormApplicationStore()
  return [
    { k: FORM_INITIAL_FLAG_PATH, v: true },
    ...formApplicationStore.baseResponses!,
  ]
}

/**
 * Safely gets a PolicyState that can be sent to the API.
 * Prefers a working copy, will use the currently loaded state if not available.
 */
export function getDataForApi (): { policyState: PolicyState, applicationFormData: ApplicationFormData } {
  const authenticationStore = useAuthenticationStore()
  const protobufStore = useProtobufStore()
  const data = protobufStore.workingCopies
    ? protobufStore.getProcessedData()
    : {
        policyState: PolicyState.clone(protobufStore.policyState as PolicyState),
        applicationFormData: ApplicationFormData.clone(protobufStore.applicationFormData as ApplicationFormData),
      }
  // include who is sending the request
  data.policyState.request = Request.create({ user: CoreUser.clone(authenticationStore.coreUser) })
  // strip out most policy data
  data.policyState.policy = Policy.create({
    id: data.policyState.policy?.id,
    uuid: data.policyState.policy?.uuid,
  })
  return data
}

/**
 * Creates endpoints related to the Application workflow.
 */
export const createApplicationEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<ApplicationApiEndpoints> => {
  const patrickServiceClient = new PatrickServiceClient(params.transport)
  const authenticationStore = useAuthenticationStore()
  const settingsStore = useSettingsStore()
  const protobufStore = useProtobufStore()
  /**
   * Reusable function for saving Protobuf application drafts.
   */
  async function saveGrpcDraft (): Promise<SubmitResponse> {
    const request = getDataForApi()
    const { response } = await patrickServiceClient.saveApplication(request)

    devtools.logGrpc({
      description: 'Update Application',
      messages: [
        { type: SaveApplicationRequest, key: 'request', message: request },
        { type: SaveApplicationResponse, key: 'response', message: response },
      ],
    })

    return await hydrateSubmitResponse(response)
  }
  /**
   * Reusable function for transitioning Protobuf application statuses.
   * Applications are transitioned by setting a specific TransactionType and GenericEventType.
   */
  async function submitGrpcTxnEvent (txn: TransactionType, options: { event?: GenericEventType, protobufOnly: true, abortController?: AbortController }): Promise<SubmitResponseProtobuf>
  async function submitGrpcTxnEvent (txn: TransactionType, options?: { event?: GenericEventType, protobufOnly?: false, abortController?: AbortController }): Promise<SubmitResponse>
  async function submitGrpcTxnEvent (txn: TransactionType, options?: { event?: GenericEventType, protobufOnly?: boolean, abortController?: AbortController }): Promise<SubmitResponse | SubmitResponseProtobuf> {
    const request = getDataForApi()

    request.policyState.txn = txn
    if (options?.event) request.policyState.event = options?.event
    const { response } = await patrickServiceClient.submitApplication(request, { abort: options?.abortController?.signal })

    devtools.logGrpc({
      description: 'Submit Application',
      messages: [
        { type: SubmitApplicationRequest, key: 'request', message: request },
        { type: SubmitApplicationResponse, key: 'response', message: response },
      ],
    })

    if (options?.protobufOnly) return response as SubmitResponseProtobuf

    return await hydrateSubmitResponse(response)
  }

  async function hydrateSubmitResponse (data: { policyState?: PolicyState, applicationFormData?: ApplicationFormData }): Promise<SubmitResponse & { policy: APIPolicy }> {
    if (!data.policyState) throw new Error('No policy state returned from API')
    const apiStore = useApiStore()

    // Get complete policy
    const { policy, json } = await apiStore.policy.read(data.policyState.policy!.id)

    data.policyState.policy = policy

    return {
      policyState: data.policyState,
      json: policyStateToApplication(data.policyState),
      policy: json as APIPolicy,
      status: data.policyState.status,
      applicationFormData: data.applicationFormData,
    }
  }

  const approveDjango: ApplicationApiEndpoints['approve'] = async (id) => {
    const { data } = await api.applications.approve({ path: { pk: id } })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const approveGrpc: ApplicationApiEndpoints['approve'] = async () => {
    // BE will update definitions to add TransactionType.APPROVE_TXN
    return submitGrpcTxnEvent(TransactionType.NULL_TXN)
  }

  const archiveDjango: ApplicationApiEndpoints['archive'] = async (id) => {
    await api.applications.inactivate({ path: { pk: id } })
  }
  const archiveGrpc: ApplicationApiEndpoints['archive'] = async () => {
    await submitGrpcTxnEvent(TransactionType.ARCHIVE_TXN)
  }

  const assignDjango: ApplicationApiEndpoints['assign'] = async (id, assignee) => {
    if (assignee) {
      const { data } = await api.applications.assign({ path: { pk: id }, body: { assignee: assignee.userDetail!.legacyId! } })
      return {
        json: data.Application as APIApplication,
        status: GenericStatus.UNKNOWN,
      }
    }

    await api.applications.remove_assign({ path: { pk: id } })
    return readDjango(id)
  }
  const assignGrpc: ApplicationApiEndpoints['assign'] = async (_id, assignee) => {
    const request = getDataForApi()
    request.policyState.txn = TransactionType.ASSIGNEE_CHANGE_TXN

    if (assignee) {
      request.policyState.assignee = patrickUserToCoreUser(assignee)
    } else {
      delete request.policyState.assignee
    }
    const { response } = await patrickServiceClient.submitApplication(request)

    devtools.logGrpc({
      description: 'Submit Application',
      messages: [
        { type: SubmitApplicationRequest, key: 'request', message: request },
        { type: SubmitApplicationResponse, key: 'response', message: response },
      ],
    })

    return await hydrateSubmitResponse(response)
  }

  const authoriseDjango: ApplicationApiEndpoints['authorise'] = async (id) => {
    const { data } = await api.applications.authorise({ path: { pk: id } })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const authoriseGrpc: ApplicationApiEndpoints['authorise'] = async () => {
    // Need to check with BE for correct TransactionType
    return submitGrpcTxnEvent(TransactionType.NULL_TXN)
  }

  const backToReviewingDjango: ApplicationApiEndpoints['backToReviewing'] = async (id) => {
    throw new Error('application.backToReviewing endpoint is not supported on Django')
  }
  const backToReviewingGrpc: ApplicationApiEndpoints['backToReviewing'] = async () => {
    return submitGrpcTxnEvent(TransactionType.BACK_TO_REVIEWING_TXN)
  }

  const bindQuoteDjango: ApplicationApiEndpoints['bindQuote'] = async () => {
    throw new Error('application.rate endpoint is not supported on Django')
  }
  const bindQuoteGrpc: ApplicationApiEndpoints['bindQuote'] = async () => {
    return submitGrpcTxnEvent(TransactionType.BIND_TXN)
  }

  const cancelDjango: ApplicationApiEndpoints['issue'] = async (id) => {
    const { data } = await api.applications.to_cancelled({ path: { pk: id } })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const cancelGrpc: ApplicationApiEndpoints['issue'] = async () => {
    return submitGrpcTxnEvent(TransactionType.CANCELLATION_TXN)
  }

  const compareDjango: ApplicationApiEndpoints['compare'] = async (baseId, targetId) => {
    const res = await api.applications.compare({ path: { pk: baseId, target_pk: targetId } })
    // @ts-expect-error(external): diff is actually nested under result, docs are incorrect
    const policyState = res.data.result.diff as DiffData
    return {
      policyState,
      formData: { added: [], changed: [], removed: [] },
    }
  }
  const compareGrpc: ApplicationApiEndpoints['compare'] = async (baseId, targetId) => {
    const policyStateDiff: DiffData = { added: [], changed: [], removed: [] }
    const formDataDiff: FormDataDiffData = { added: [], changed: [], removed: [] }
    const request = DiffPolicyStatesRequest.create({ baseId, targetId })
    const { response } = await patrickServiceClient.diffPolicyStates(request)

    devtools.logGrpc({
      description: 'Diff Policy States',
      messages: [
        { type: DiffPolicyStatesRequest, key: 'request', message: request },
        { type: DiffPolicyStatesResponse, key: 'response', message: response },
      ],
    })

    for (const { k, currentValue } of processDiffResults(response.policyState?.added ?? {})) {
      policyStateDiff.added.push({ k, uuid4: k, v: currentValue })
    }
    for (const { k, previousValue, currentValue } of processDiffResults(response.policyState?.changed ?? {})) {
      policyStateDiff.changed.push({
        uuid4: k,
        changed: {
          k: [k, k],
          v: [previousValue, currentValue],
        },
      })
    }
    for (const { k, previousValue } of processDiffResults(response.policyState?.removed ?? {})) {
      policyStateDiff.removed.push({ k, uuid4: k, v: previousValue })
    }

    const toCamelKey = (k: string): string => k.split('.').map((part) => toCamelCase(part)).join('.')
    for (const [k, entry] of Object.entries(response.formData?.added ?? {})) {
      if (entry.currentValue) formDataDiff.added.push({ k: toCamelKey(k), v: entry.currentValue })
    }
    for (const [k, entry] of Object.entries(response.formData?.changed ?? {})) {
      if (entry.previousValue && entry.currentValue) {
        formDataDiff.changed.push({ k: toCamelKey(k), v: [entry.previousValue, entry.currentValue] })
      }
    }
    for (const [k, entry] of Object.entries(response.formData?.removed ?? {})) {
      if (entry.previousValue) formDataDiff.removed.push({ k: toCamelKey(k), v: entry.previousValue })
    }

    return { policyState: policyStateDiff, formData: formDataDiff }
  }

  const createDjango: ApplicationApiEndpoints['create'] = async (currentProgram, payload) => {
    const responses = createBaseResponses()
    responses.push(...payload.serialize())

    const res = await api.policies.create({
      body: objectToFormData({
        program: currentProgram.id,
        data: { responses },
      }),
    })

    return {
      json: res.data.Application,
      status: GenericStatus.UNKNOWN,
    }
  }
  const createGrpc: ApplicationApiEndpoints['create'] = async (currentProgram) => {
    const program = Program.create({
      id: currentProgram.id,
      name: currentProgram.name,
      slug: currentProgram.slug,
      ulid: currentProgram.bingoId,
    })
    const user = CoreUser.clone(authenticationStore.coreUser)

    protobufStore.setValue({ path: 'program' }, program, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
    protobufStore.setValue({ path: 'owner' }, user, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
    const request = protobufStore.getProcessedData()
    request.policyState.txn = TransactionType.SPAWN_TXN

    if (Sentry && !request.policyState.owner?.id && !request.policyState.owner?.ulid) {
      Sentry.captureException(`Owner ids not set on spawn! ID: ${request.policyState.owner?.id}, ULID: ${request.policyState.owner?.ulid}`)
    }

    const { response } = await patrickServiceClient.spawnApplication(request)

    devtools.logGrpc({
      description: 'Create Application',
      messages: [
        { type: SpawnApplicationRequest, key: 'request', message: request },
        { type: SpawnApplicationResponse, key: 'response', message: response },
      ],
    })

    return await hydrateSubmitResponse(response)
  }

  const declineDjango: ApplicationApiEndpoints['decline'] = async (id) => {
    const { data } = await api.applications.decline({ path: { pk: id }, body: {} })

    return {
      json: data.Application,
      status: GenericStatus.UNKNOWN,
    }
  }
  const declineGrpc: ApplicationApiEndpoints['decline'] = async () => {
    return submitGrpcTxnEvent(TransactionType.DECLINE_TXN)
  }

  const deleteDjango: ApplicationApiEndpoints['delete'] = async (id) => {
    await api.applications.delete({ path: { pk: id } })
  }
  const deleteGrpc: ApplicationApiEndpoints['delete'] = async () => {
    await submitGrpcTxnEvent(TransactionType.ARCHIVE_TXN)
  }

  const draftDjango: ApplicationApiEndpoints['draft'] = async (id) => {
    const { data } = await api.applications.to_draft({ path: { pk: id } })

    return {
      json: data.Application,
      status: GenericStatus.UNKNOWN,
    }
  }
  const draftGrpc: ApplicationApiEndpoints['draft'] = async () => {
    return submitGrpcTxnEvent(TransactionType.DRAFT_TXN)
  }

  const finaliseDjango: ApplicationApiEndpoints['finalise'] = async (id) => {
    const { data } = await api.applications.finalise({ path: { pk: id } })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const finaliseGrpc: ApplicationApiEndpoints['finalise'] = async () => {
    return submitGrpcTxnEvent(TransactionType.AUTHORISE_TXN)
  }

  const freshnessDjango: ApplicationApiEndpoints['freshness'] = async (id) => {
    const res = await api.applications.freshness({ path: { pk: id } })
    return res.data.modified
  }
  const freshnessGrpc: ApplicationApiEndpoints['freshness'] = async (id, abortController) => {
    const request = ReadPolicyStateRequest.create({ id })
    const { response } = await patrickServiceClient.readPolicyState(request, { abort: abortController?.signal })
    return response.policyState?.resourceVersion ?? '0'
  }

  const generateQuoteDocDjango: ApplicationApiEndpoints['generateQuoteDoc'] = async (id) => {
    await api.applications.generate_quote_doc({ path: { pk: id } })
    return readDjango(id)
  }
  const generateQuoteDocGrpc: ApplicationApiEndpoints['generateQuoteDoc'] = async () => {
    return submitGrpcTxnEvent(TransactionType.GENERATE_DOCUMENT_TXN)
  }

  const issueDjango: ApplicationApiEndpoints['issue'] = async (id, body, fuzzy) => {
    const { data } = await api.applications.to_issued({
      path: { pk: id },
      ...(body
        ? {
            body: objectToFormData({
              responses: body,
              policy_state: protobufStore.serializePolicyState(),
            }),
          }
        : {}),
      query: fuzzy ? { mode: 'fuzzy' } : {},
    })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const issueGrpc: ApplicationApiEndpoints['issue'] = async (_id, _body, fuzzy) => {
    if (fuzzy) return await saveGrpcDraft()
    return submitGrpcTxnEvent(TransactionType.ISSUANCE_TXN)
  }

  const issuanceDataDjango: ApplicationApiEndpoints['issuanceData'] = async (id, body, fuzzy) => {
    const { data } = await api.applications.apply_issuance_data({
      path: { pk: id },
      body: objectToFormData({
        data: { responses: body },
        policy_state: protobufStore.serializePolicyState(),
      }),
      query: fuzzy ? { mode: 'fuzzy' } : {},
    })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const issuanceDataGrpc: ApplicationApiEndpoints['issuanceData'] = async (_id, _body, fuzzy) => {
    if (fuzzy) return await saveGrpcDraft()
    return submitGrpcTxnEvent(TransactionType.ISSUANCE_TXN)
  }

  const lockDjango: ApplicationApiEndpoints['lock'] = async (id) => {
    await api.applications.lock({
      path: { pk: id },
      // @ts-expect-error(external): lock_code can be null, docs are incorrect
      body: { lock_code: null },
    })

    return readDjango(id)
  }
  const lockGrpc: ApplicationApiEndpoints['lock'] = async () => {
    const request = getDataForApi()
    // TODO: BE will update definitions to add lock info, we will need to set that

    const { response } = await patrickServiceClient.saveApplication(request)

    devtools.logGrpc({
      description: 'Lock Application',
      messages: [
        { type: SaveApplicationRequest, key: 'request', message: request },
        { type: SaveApplicationResponse, key: 'response', message: response },
      ],
    })

    if (!response.policyState) throw new Error('No policy state returned from API')

    return {
      responses: [],
      policyState: response.policyState,
      json: policyStateToApplication(response.policyState),
    }
  }

  const lostDjango: ApplicationApiEndpoints['lost'] = async (id) => {
    const { data } = await api.applications.lost({ path: { pk: id }, body: {} })

    return {
      json: data.Application,
      status: GenericStatus.UNKNOWN,
    }
  }
  const lostGrpc: ApplicationApiEndpoints['lost'] = async () => {
    return submitGrpcTxnEvent(TransactionType.QUOTE_LOST_TXN)
  }

  const pendingIssueDjango: ApplicationApiEndpoints['pendingIssue'] = async (id, responses, binary) => {
    let body = responses
      ? { data: { responses: responses.serialize() } }
      : undefined

    // @ts-expect-error(fixable): Cannot currently match types of FormData
    body = (body && binary)
      ? objectToFormData({ ...body, binary })
      : body

    const { data } = await api.applications.to_pending_issue({
      path: { pk: id },
      ...(body ? { body } : {}),
      query: {},
    })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.REQUESTED,
    }
  }
  const pendingIssueGrpc: ApplicationApiEndpoints['pendingIssue'] = async () => {
    // Will need to send some additional info to BE
    return submitGrpcTxnEvent(TransactionType.BIND_TXN)
  }

  const qualityControlDjango: ApplicationApiEndpoints['qualityControl'] = async (id, action) => {
    const { data } = await api.applications.quality_control({ path: { pk: id }, body: { action } })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const qualityControlGrpc: ApplicationApiEndpoints['qualityControl'] = async (_id, action) => {
    switch (action) {
      case 'PASS':
        return submitGrpcTxnEvent(TransactionType.QC_PASS_TXN)
      case 'FAIL':
        return submitGrpcTxnEvent(TransactionType.QC_FAIL_TXN)
      case 'SKIP':
        return submitGrpcTxnEvent(TransactionType.QC_SKIP_TXN)
      default:
        throw new Error(`Invalid quality control action: ${action}. Must be PASS, FAIL, or SKIP`)
    }
  }

  const qualityRecheckDjango: ApplicationApiEndpoints['qualityRecheck'] = async (id) => {
    const { data } = await api.applications.force_quality_recheck({ path: { pk: id } })

    return {
      // @ts-expect-error(fixable): Attachment types are incorrect
      json: data.Application as APIApplication,
      status: GenericStatus.UNKNOWN,
    }
  }
  const qualityRecheckGrpc: ApplicationApiEndpoints['qualityRecheck'] = async () => {
    return submitGrpcTxnEvent(TransactionType.BACK_TO_QC_TXN)
  }

  const quoteDjango: ApplicationApiEndpoints['quote'] = async (id, body, _kind, fuzzy) => {
    const { data } = await api.applications.quote({
      path: { pk: id },
      body,
      query: fuzzy ? { mode: 'fuzzy' } : {},
    })

    return {
      json: data.Application,
      status: GenericStatus.UNKNOWN,
    }
  }
  const quoteGrpc: ApplicationApiEndpoints['quote'] = async (_id, _quotes, kind, fuzzy) => {
    let txn = TransactionType.QUOTE_TXN
    switch (true) {
      // Saving a draft
      case fuzzy:
        txn = TransactionType.SAVE_DRAFT_TXN
        break
      case kind === Kind.KIND_ENDORSEMENT:
        txn = TransactionType.ENDORSEMENT_QUOTE_TXN
        break
      case kind === Kind.KIND_CANCELLATION:
        txn = TransactionType.CANCELLATION_QUOTE_TXN
        break
      case kind === Kind.KIND_EXTENSION:
        txn = TransactionType.EXTENSION_QUOTE_TXN
        break
    }

    return await submitGrpcTxnEvent(txn)
  }

  const rateDjango: ApplicationApiEndpoints['rate'] = async () => {
    throw new Error('application.rate endpoint is not supported on Django')
  }
  const rateGrpc: ApplicationApiEndpoints['rate'] = async (txn, abortController) => {
    return await submitGrpcTxnEvent(txn, { protobufOnly: true, abortController })
  }

  const readDjango: ApplicationApiEndpoints['read'] = async (id) => {
    const res = await api.applications.read({ path: { pk: id }, query: {} })
    return {
      json: res.data.Application,
      status: GenericStatus.UNKNOWN,
    }
  }
  const readGrpc: ApplicationApiEndpoints['read'] = async (id, abortController) => {
    const request = ReadPolicyStateRequest.create({ id })
    const { response } = await patrickServiceClient.readPolicyState(request, { abort: abortController?.signal })
    const policyState = PolicyState.create(response.policyState)

    // redirect to homepage if current program does not match policy state program
    if (!hasMatchingId(policyState.program ?? null, Program.create({ id: authenticationStore.programIds.id, ulid: authenticationStore.programIds.bingoId }))) {
      const code = 'PROGRAM_ID_MISMATCH'
      throw new AxiosError('', code, undefined, undefined, {
        status: 400,
        statusText: '',
        data: { error: { code } },
        headers: {},
        config: { headers: {} as AxiosResponseHeaders },
      })
    }

    devtools.logGrpc({
      description: 'Read Application',
      messages: [
        { type: RetrieveByIdRequest, key: 'request', message: request },
        { type: PolicyState, key: 'response', message: policyState },
      ],
    })

    return await hydrateSubmitResponse(response)
  }

  const resubmitDjango: ApplicationApiEndpoints['resubmit'] = async (id, payload, _type, _rate, status) => {
    const { json } = await updateDjango(id, payload, status, false)

    return {
      json: json!,
      status: applicationStatusToGenericStatus(json!.status as ApplicationStatus, !!json?.is_cancellation),
    }
  }
  const resubmitGrpc: ApplicationApiEndpoints['resubmit'] = async (id, payload, type, rate) => {
    return submitGrpcTxnEvent((type === 'APP_PENDING_INFO' || type === 'APP_RENEWAL_PENDING_INFO')
      ? TransactionType.BIND_AMENDED_TXN
      : TransactionType.INQUIRY_TXN)
  }

  const saveDjango: ApplicationApiEndpoints['save'] = async (id, payload, status, fuzzy) => {
    return updateDjango(id, payload, status, fuzzy)
  }
  const saveGrpc: ApplicationApiEndpoints['save'] = async () => {
    const response = await submitGrpcTxnEvent(TransactionType.SAVE_DRAFT_TXN)
    return {
      ...response,
      responses: [],
    }
  }

  const selectQuoteDjango: ApplicationApiEndpoints['selectQuote'] = async (id, quoteUuid, payload) => {
    const res = await api.applications.selectquote({
      path: { pk: id },
      body: objectToFormData({
        selected: [quoteUuid],
        ...(payload
          ? { injected_response: payload.serialize() }
          : {}),
      }),
    })

    return {
      json: res.data.Application,
    }
  }
  const selectQuoteGrpc: ApplicationApiEndpoints['selectQuote'] = async (_id, quoteUuid) => {
    const request = protobufStore.getProcessedData()
    request.policyState.selectedQuote = request.policyState.quotes!.quotes!.find((q: { uuid4: string }) => q.uuid4 === quoteUuid)

    const { response } = await patrickServiceClient.submitApplication(request)

    devtools.logGrpc({
      description: 'Select Quote',
      messages: [
        { type: SubmitApplicationRequest, key: 'request', message: request },
        { type: SubmitApplicationResponse, key: 'response', message: response },
      ],
    })

    return await hydrateSubmitResponse(response)
  }

  const spawnDjango: ApplicationApiEndpoints['spawn'] = async (id, txn, cancellationReason) => {
    let body: paths['/api/v1/applications/spawn/']['post']['requestBody']['content']['application/json']
    switch (txn) {
      case TransactionType.CANCELLATION_SPAWN_TXN:
        body = {
          application_id: id,
          kind: 'ENDORSEMENT',
          new_policy: false,
          clear_app_data: false,
          clear_quote_data: true,
          clear_derived_data: true,
          is_cancellation: true,
          injected_responses: cancellationReason,
        }
        break
      case TransactionType.ENDORSEMENT_SPAWN_TXN:
        body = {
          application_id: id,
          kind: 'ENDORSEMENT',
          new_policy: false,
          clear_app_data: false,
          clear_quote_data: true,
          clear_derived_data: true,
          injected_responses: [{ k: FORM_INITIAL_FLAG_PATH, v: true }],
        }
        break
      case TransactionType.RENEWAL_SPAWN_TXN:
        body = {
          application_id: id,
          kind: 'RENEWAL',
          new_policy: settingsStore.renewsToNewPolicy,
          clear_app_data: false,
          clear_quote_data: true,
          clear_derived_data: true,
          terminate_self: false,
          archive_self: false,
          injected_responses: [{ k: FORM_INITIAL_FLAG_PATH, v: true }],
        }
        break
      case TransactionType.RECLAIM_SPAWN_TXN:
        body = {
          application_id: id,
          kind: 'RECLAIM',
          new_policy: true,
          clear_app_data: false,
          clear_quote_data: true,
          clear_derived_data: true,
          terminate_self: false,
          archive_self: false,
        }
        break
      default:
        body = {
          application_id: id,
          kind: 'APPLICATION',
          new_policy: true,
          clear_app_data: false,
          clear_quote_data: true,
          clear_derived_data: true,
        }
    }

    const { data } = await api.applications.spawn({ body })

    return {
      json: data.Application,
      policy: data.Policy,
    }
  }
  const spawnGrpc: ApplicationApiEndpoints['spawn'] = async (_id, txn = TransactionType.SPAWN_TXN) => {
    const apiStore = useApiStore()
    const request = getDataForApi()
    request.policyState.id = 0
    request.policyState.uuid = ''
    request.policyState.txn = txn

    switch (txn) {
      case TransactionType.CANCELLATION_SPAWN_TXN: {
        const { policy } = await apiStore.policy.read(request.policyState.policy!.id)
        request.policyState.policy = policy
        request.policyState.kind = Kind.KIND_ENDORSEMENT
        break
      }
      case TransactionType.START_OVER_TXN:
      case TransactionType.REPLICATE_SPAWN_TXN:
      case TransactionType.SPAWN_TXN: {
        request.policyState.policy = undefined
        break
      }
      case TransactionType.ENDORSEMENT_SPAWN_TXN: {
        const { policy } = await apiStore.policy.read(request.policyState.policy!.id)
        request.policyState.policy = policy
        request.policyState.kind = Kind.KIND_ENDORSEMENT
        break
      }
      case TransactionType.RENEWAL_SPAWN_TXN: {
        const { policy } = await apiStore.policy.read(request.policyState.policy!.id)
        request.policyState.policy = Policy.create({
          parent: policy,
        })
        request.policyState.kind = Kind.KIND_RENEWAL
        break
      }
      case TransactionType.RECLAIM_SPAWN_TXN: {
        const { policy } = await apiStore.policy.read(request.policyState.policy!.id)
        request.policyState.policy = Policy.create({
          parent: policy,
        })
        request.policyState.kind = Kind.KIND_RECLAIM
        break
      }
      case TransactionType.EXTENSION_SPAWN_TXN: {
        const { policy } = await apiStore.policy.read(request.policyState.policy!.id)
        request.policyState.policy = Policy.create(policy)
        request.policyState.kind = Kind.KIND_EXTENSION
        break
      }
    }

    const { response } = await patrickServiceClient.spawnApplication(request)

    devtools.logGrpc({
      description: 'Spawn Application',
      messages: [
        { type: SpawnApplicationRequest, key: 'request', message: request },
        { type: SpawnApplicationResponse, key: 'response', message: response },
      ],
    })

    return await hydrateSubmitResponse(response)
  }

  const submitDjango: ApplicationApiEndpoints['submit'] = async (id, payload, kind, rate) => {
    let formData

    const { json, responses } = await updateDjango(id, payload, 'DRAFT', false)
    const rateResult = await rate(json as APIApplication, responses)

    // Django program uses GPRC rating service, pass formData so rating can be done server side
    if (rateResult) {
      const status = rateResult.response.decision === QuoteDecision.REFER_QUOTE_DEC || kind === 'ENDORSEMENT' ? 'REVIEW' : 'QUOTED'
      formData = objectToFormData({
        status,
        binary: rateResult.binaryQuoteSet,
      })
      if (status === 'QUOTED') {
        const quotes = rateResult.quotes.map((q) => ({ responses: q.serialize() }))
        formData.append('quote_data', JSON.stringify({ quotes }))
      }
    }
    // @ts-expect-error(external): API says body is required
    const { data } = await api.applications.submit({ path: { pk: id }, body: formData })

    return {
      json: data.Application,
      status: applicationStatusToGenericStatus(data.Application.status as ApplicationStatus, !!data.Application?.is_cancellation),
    }
  }
  const submitGrpc: ApplicationApiEndpoints['submit'] = async (_id, _payload, kind) => {
    return await submitGrpcTxnEvent(
      kind === 'ENDORSEMENT' ? TransactionType.ENDORSEMENT_INQUIRY_TXN : TransactionType.INQUIRY_TXN,
      { event: GenericEventType.INQUIRY_EVENT },
    )
  }

  const updateDjango: ApplicationApiEndpoints['update'] = async (id, payload, status, fuzzy) => {
    if (!id || !payload) throw new Error('Application id and payload required for django update endpoint')

    const responses = payload.serialize()
    const body = objectToFormData({
      data: { responses },
    })
    if (status === 'REQUESTED_TO_BIND') body.set('transition', 'true')

    const { data } = await api.applications.update({
      path: { pk: id },
      body,
      query: fuzzy ? { mode: 'fuzzy' } : {}, // "fuzzy" update doesn't return any value from api
    })

    const app = data.Application as APIApplication | undefined

    return {
      json: app,
      responses: app?.computedData?.responses ?? payload?.serialize(),
      status: applicationStatusToGenericStatus(app?.status as ApplicationStatus, !!app?.is_cancellation),
    }
  }
  const updateGrpc: ApplicationApiEndpoints['update'] = async () => {
    const response = await saveGrpcDraft()
    return {
      ...response,
      responses: [],
    }
  }

  const workflowDjango: ApplicationApiEndpoints['workflow'] = async () => {
    throw new Error('Workflow not supported in Django')
  }
  const workflowGrpc: ApplicationApiEndpoints['workflow'] = async (input, options = { protobufOnly: true }) => {
    const data = getDataForApi()
    const request = WorkflowRequest.create({
      ...data,
      program: authenticationStore.slug,
      name: input.name,
      additionalArgs: input.additionalArgs,
    })

    const { response } = await patrickServiceClient.workflow(request, { abort: options.abortController?.signal })

    if (!response.policyState) {
      throw new Error('Workflow did not return a policy state')
    }

    devtools.logGrpc({
      description: 'Workflow',
      messages: [
        { type: WorkflowRequest, key: 'request', message: request },
        { type: WorkflowResponse, key: 'response', message: response },
      ],
    })

    const result = options.protobufOnly
      ? response
      : await hydrateSubmitResponse(response)

    return result as SubmitResponse & SubmitResponseProtobuf
  }

  return {
    django: {
      approve: approveDjango,
      archive: archiveDjango,
      assign: assignDjango,
      authorise: authoriseDjango,
      backToReviewing: backToReviewingDjango,
      bindQuote: bindQuoteDjango,
      cancel: cancelDjango,
      compare: compareDjango,
      create: createDjango,
      delete: deleteDjango,
      decline: declineDjango,
      draft: draftDjango,
      finalise: finaliseDjango,
      freshness: freshnessDjango,
      generateQuoteDoc: generateQuoteDocDjango,
      issue: issueDjango,
      issuanceData: issuanceDataDjango,
      lock: lockDjango,
      lost: lostDjango,
      pendingIssue: pendingIssueDjango,
      qualityControl: qualityControlDjango,
      qualityRecheck: qualityRecheckDjango,
      quote: quoteDjango,
      rate: rateDjango,
      read: readDjango,
      resubmit: resubmitDjango,
      save: saveDjango,
      selectQuote: selectQuoteDjango,
      spawn: spawnDjango,
      submit: submitDjango,
      update: updateDjango,
      workflow: workflowDjango,
    },
    grpc: {
      approve: approveGrpc,
      archive: archiveGrpc,
      assign: assignGrpc,
      authorise: authoriseGrpc,
      backToReviewing: backToReviewingGrpc,
      bindQuote: bindQuoteGrpc,
      cancel: cancelGrpc,
      compare: compareGrpc,
      create: createGrpc,
      delete: deleteGrpc,
      decline: declineGrpc,
      draft: draftGrpc,
      finalise: finaliseGrpc,
      freshness: freshnessGrpc,
      generateQuoteDoc: generateQuoteDocGrpc,
      issue: issueGrpc,
      issuanceData: issuanceDataGrpc,
      lock: lockGrpc,
      lost: lostGrpc,
      pendingIssue: pendingIssueGrpc,
      qualityControl: qualityControlGrpc,
      qualityRecheck: qualityRecheckGrpc,
      quote: quoteGrpc,
      rate: rateGrpc,
      read: readGrpc,
      resubmit: resubmitGrpc,
      save: saveGrpc,
      selectQuote: selectQuoteGrpc,
      spawn: spawnGrpc,
      submit: submitGrpc,
      update: updateGrpc,
      workflow: workflowGrpc,
    },
  }
}
